"use client"

import { useState, useEffect } from "react"
import {
    addDays,
    addMonths,
    endOfMonth,
    endOfWeek,
    format,
    isBefore,
    isAfter,
    isSameMonth,
    isToday,
    startOfMonth,
    startOfWeek,
    subMonths,
} from "date-fns"
import { zhTW } from "date-fns/locale"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "./utils"
// import { HolidayTooltip } from "./holiday-tooltip"
import { Button } from "./ot"



export function CalendarGrid({
    holidays = [],
    dateRange,
    onDateChange
}) {
    const [rows, setRows] = useState([])
    const [currentDate, setCurrentDate] = useState(() => {
        const today = new Date()
        // Check if today is within the allowed range
        if (isBefore(today, dateRange?.minDate)) {
            return dateRange?.minDate
        } else if (isAfter(today, dateRange?.maxDate)) {
            return dateRange?.maxDate
        }
        return today
    })


    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 })
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 })

    const weekdays = ["日", "一", "二", "三", "四", "五", "六"]

    // 導航功能
    const goToPreviousMonth = () => {
        const newDate = subMonths(currentDate, 1)
        if (!isBefore(newDate, dateRange?.minDate)) {
            setCurrentDate(newDate)
        }
    }

    const goToNextMonth = () => {
        const newDate = addMonths(currentDate, 1)
        if (!isAfter(newDate, dateRange?.maxDate)) {
            setCurrentDate(newDate)
        }
    }

    const goToToday = () => {
        const today = new Date()
        // 確保今天在允許的範圍內
        if (isBefore(today, dateRange?.minDate)) {
            setCurrentDate(dateRange?.minDate)
        } else if (isAfter(today, dateRange?.maxDate)) {
            setCurrentDate(dateRange?.maxDate)
        } else {
            setCurrentDate(today)
        }
    }

    // 生成年份選項
    const generateYearOptions = () => {
        const minYear = dateRange?.minDate.getFullYear()
        const maxYear = dateRange?.maxDate.getFullYear()
        const years = []

        for (let year = minYear; year <= maxYear; year++) {
            years.push(year)
        }

        return years
    }

    // 生成月份選項
    const generateMonthOptions = () => {
        const currentYear = currentDate.getFullYear()
        const minYear = dateRange?.minDate.getFullYear()
        const maxYear = dateRange?.maxDate.getFullYear()

        // 如果當前年份是最小年份，則從最小日期的月份開始
        let startMonth = 0
        if (currentYear === minYear) {
            startMonth = dateRange?.minDate.getMonth()
        }

        // 如果當前年份是最大年份，則到最大日期的月份結束
        let endMonth = 11
        if (currentYear === maxYear) {
            endMonth = dateRange?.maxDate.getMonth()
        }

        const months = []
        for (let month = startMonth; month <= endMonth; month++) {
            months.push(month)
        }

        return months
    }

    const isPreviousMonthDisabled = isBefore(subMonths(currentDate, 1), dateRange?.minDate)
    const isNextMonthDisabled = isAfter(addMonths(currentDate, 1), dateRange?.maxDate)
    useEffect(() => {
        // 創建日曆網格
        let rows = []
        let days = []
        let day = startDate
        let formattedDate = ""

        while (day <= endDate) {
            for (let i = 0; i < 7; i++) {
                formattedDate = format(day, "d")
                const dateString = format(day, "yyyy-MM-dd")
                const dayHolidays = holidays.filter((holiday) => holiday?.date === dateString)

                days.push(
                    <div
                        key={day.toString()}
                        className={cn(
                            "relative h-32 border p-1 transition-all hover:bg-accent/50 md:h-24",
                            !isSameMonth(day, monthStart) && "bg-muted/50 text-muted-foreground",
                            isToday(day) && "border-primary",
                        )}
                    >
                        <div className="flex justify-between">
                            <span
                                className={cn(
                                    "inline-flex h-6 w-6 items-center justify-center rounded-full text-md",
                                    isToday(day) && "bg-primary text-primary-foreground",
                                )}
                            >
                                {formattedDate}
                            </span>
                        </div>

                        {dayHolidays.length > 0 && (
                            <div className="mt-1 space-y-1">
                                {dayHolidays.map((holiday, index) => (
                                    // <HolidayTooltip key={index} holiday={holiday}>
                                    
                                    <div
                                        className={cn(
                                            "rounded px-1 py-0.5 text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap",
                                            `${holiday?.color}`,
                                        )}
                                    >
                                        {holiday?.name}
                                    </div>
                                    // </HolidayTooltip>
                                ))}
                            </div>
                        )}
                    </div>,
                )
                day = addDays(day, 1)
            }

            rows.push(
                <div key={day.toString()} className="grid grid-cols-7">
                    {days}
                </div>,
            )
            days = []
        }
        setRows(rows)
    }, [holidays])

    // 當日期變更時通知父元件
    useEffect(() => {
        if (onDateChange) {
            onDateChange(currentDate)
        }
    }, [currentDate, onDateChange])
    useEffect(() => {
        // When dateRange changes, ensure currentDate is still valid
        if (isBefore(currentDate, dateRange?.minDate)) {
            setCurrentDate(dateRange?.minDate)
        } else if (isAfter(currentDate, dateRange?.maxDate)) {
            setCurrentDate(dateRange?.maxDate)
        }
    }, [dateRange, currentDate])
    return (
        <div className="space-y-4">
            {/* 日期導航控制項 */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-2xl font-semibold">{format(currentDate, "yyyy年 MMMM", { locale: zhTW })}</h2>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={goToPreviousMonth} disabled={isPreviousMonthDisabled}>
                        <ChevronLeft className="h-4 w-4" />
                        <span className="sr-only">上個月</span>
                    </Button>
                    {/* <Button variant="outline" size="sm" onClick={goToToday}>
                        今天
                    </Button> */}
                    <Button variant="outline" size="sm" onClick={goToNextMonth} disabled={isNextMonthDisabled}>
                        <ChevronRight className="h-4 w-4" />
                        <span className="sr-only">下個月</span>
                    </Button>
                    <select
                        className="h-9 rounded-md border border-input bg-background px-3 py-1 text-md shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        value={currentDate.getMonth()}
                        onChange={(e) => {
                            const newMonth = Number.parseInt(e.target.value)
                            const newDate = new Date(currentDate)
                            newDate.setMonth(newMonth)
                            setCurrentDate(newDate)
                        }}
                    >
                        {generateMonthOptions().map((month) => {
                            const monthDate = new Date(currentDate.getFullYear(), month, 1)
                            return (
                                <option key={month} value={month}>
                                    {format(monthDate, "MMMM", { locale: zhTW })}
                                </option>
                            )
                        })}
                    </select>
                    <select
                        className="h-9 rounded-md border border-input bg-background px-3 py-1 text-md shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        value={currentDate.getFullYear()}
                        onChange={(e) => {
                            const newYear = Number.parseInt(e.target.value)
                            const newDate = new Date(currentDate)
                            newDate.setFullYear(newYear)

                            // 檢查月份是否在範圍內
                            if (newYear === dateRange?.minDate.getFullYear() && newDate.getMonth() < dateRange?.minDate.getMonth()) {
                                newDate.setMonth(dateRange?.minDate.getMonth())
                            } else if (
                                newYear === dateRange?.maxDate.getFullYear() &&
                                newDate.getMonth() > dateRange?.maxDate.getMonth()
                            ) {
                                newDate.setMonth(dateRange?.maxDate.getMonth())
                            }

                            setCurrentDate(newDate)
                        }}
                    >
                        {generateYearOptions().map((year) => (
                            <option key={year} value={year}>
                                {year}年
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* 日曆網格 */}
            <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                <div className="grid grid-cols-7 gap-px border-b bg-muted">
                    {weekdays.map((weekday, i) => (
                        <div
                            key={i}
                            className={cn("py-2 text-center text-md font-medium", i === 0 || i === 6 ? "text-red-500" : "")}
                        >
                            {weekday}
                        </div>
                    ))}
                </div>
                <div className="divide-y">{rows}</div>
            </div>
        </div>
    )
}
